import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Avatar, Dropdown, DropdownOption, InlineAssignmentsComponent, ToastService } from '@zag/ui';
import { type GroupMember } from '../groups-management.service';
import { GroupsManagementService } from '../groups-management.service';
import { UsersManagementService } from '../users-management.service';

@Component({
  selector: 'sc-group-member-list',
  imports: [InlineAssignmentsComponent, Avatar, Dropdown, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <zag-inline-assignments
      [items]="groupsService.allMembers()"
      [loading]="groupsService.membersLoading()"
      [disabled]="isSaving()"
      [emptyMessage]="emptyMessageKey() | translate"
      [removeLabel]="'common.remove' | translate"
      (remove)="onRemove($event)"
    >
      <ng-template let-member>
        <div class="group-member-list__row" [class.group-member-list__row--local]="isLocalMemberAddition(asMember(member))">
          <zag-avatar [name]="asMember(member).user.name" size="sm" class="group-member-list__avatar" />
          <div class="group-member-list__info">
            <span class="group-member-list__name">{{ asMember(member).user.name }}</span>
            <span class="group-member-list__email">{{ asMember(member).user.email }}</span>
          </div>
          @if (isLocalMemberAddition(asMember(member))) {
            <span class="group-member-list__badge">{{ pendingBadgeKey() | translate }}</span>
          }
        </div>
      </ng-template>
      <div slot="add-form" class="group-member-list__add-form">
        <zag-dropdown
          [options]="memberOptions()"
          [value]="selectedUserId()"
          [disabled]="isSaving() || unassignedLoading() || memberOptions().length === 0"
          (selectionChange)="onAdd($event)"
          [placeholder]="addLabelKey() | translate"
        />
        @if (memberOptions().length === 0 && !unassignedLoading()) {
          <p class="group-member-list__no-items">{{ noUnassignedKey() | translate }}</p>
        }
      </div>
    </zag-inline-assignments>
  `,
  styleUrl: './group-member-list.component.scss',
})
export class GroupMemberListComponent {
  readonly groupId = input<string | null>(null);
  readonly isNew = input<boolean>(false);
  readonly isSaving = input<boolean>(false);
  readonly emptyMessageKey = input<string>('settings.groups.noMembers');
  readonly addLabelKey = input<string>('settings.groups.addMember');
  readonly noUnassignedKey = input<string>('settings.groups.noUnassignedUsers');
  readonly addedToastKey = input<string>('settings.groups.memberAddedLocal');
  readonly pendingBadgeKey = input<string>('settings.groups.pendingBadge');

  protected readonly groupsService = inject(GroupsManagementService);
  protected readonly usersService = inject(UsersManagementService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  protected readonly selectedUserId = signal<string | null>(null);

  readonly memberCount = computed(() => this.groupsService.allMembers().length);

  protected readonly memberOptions = computed<DropdownOption<string>[]>(() => {
    if (this.isNew()) {
      const addedIds = new Set(this.groupsService.localAddedMembers().map(m => m.userId));
      return this.usersService
        .users()
        .filter(u => !addedIds.has(u.id))
        .map(u => ({ label: `${u.name} (${u.email})`, value: u.id }));
    }
    return this.groupsService.availableUnassignedUsers().map(u => ({ label: `${u.name} (${u.email})`, value: u.id }));
  });

  protected readonly unassignedLoading = computed(() =>
    this.isNew() ? this.usersService.isLoading() : this.groupsService.unassignedLoading()
  );

  constructor() {
    effect(() => {
      this.groupId();
      this.selectedUserId.set(null);
    });
  }

  protected asMember(item: unknown): GroupMember {
    return item as GroupMember;
  }

  protected isLocalMemberAddition(member: GroupMember): boolean {
    return this.groupsService.localAddedMembers().some(m => m.userId === member.userId);
  }

  protected onAdd(userId: string | null): void {
    if (!userId) return;
    const source = this.isNew() ? this.usersService.users() : this.groupsService.availableUnassignedUsers();
    const user = source.find(u => u.id === userId);
    if (user) {
      this.groupsService.addMemberLocally({ id: user.id, name: user.name, email: user.email }, false);
      this.toast.success(this.translate.instant(this.addedToastKey()));
    }
    this.selectedUserId.set(null);
  }

  protected onRemove(index: number): void {
    const member = this.groupsService.allMembers()[index];
    if (member) this.groupsService.removeMemberLocally(member.userId);
  }
}
